/**
 * IndexedDB Storage Adapter for Zustand
 * 使用 IndexedDB 替代 localStorage 以获得更大的存储容量
 */
import { openDB, DBSchema, IDBPDatabase } from 'idb'

/**
 * 数据库 Schema
 */
interface MarkflowDB extends DBSchema {
  keyvalue: {
    key: string
    value: any
  }
}

/**
 * 数据库名称和版本
 */
const DB_NAME = 'markflow-db'
const DB_VERSION = 1
const STORE_NAME = 'keyvalue'

/**
 * IndexedDB Storage Adapter
 * 实现与 zustand persist middleware 兼容的 storage 接口
 */
class IndexedDBStorage {
  private dbPromise: Promise<IDBPDatabase<MarkflowDB>> | null = null

  /**
   * 获取数据库实例
   */
  private getDB(): Promise<IDBPDatabase<MarkflowDB>> {
    if (!this.dbPromise) {
      this.dbPromise = openDB<MarkflowDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          // 创建 key-value 存储
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME)
          }
        },
      })
    }
    return this.dbPromise
  }

  /**
   * 读取数据
   */
  async getItem(key: string): Promise<string | null> {
    try {
      const db = await this.getDB()
      const value = await db.get(STORE_NAME, key)
      return value ?? null
    } catch (error) {
      console.error(`IndexedDB read failed: ${key}`, error)
      return null
    }
  }

  /**
   * 写入数据
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      const db = await this.getDB()
      await db.put(STORE_NAME, value, key)
    } catch (error) {
      console.error(`IndexedDB write failed: ${key}`, error)
    }
  }

  /**
   * 删除数据
   */
  async removeItem(key: string): Promise<void> {
    try {
      const db = await this.getDB()
      await db.delete(STORE_NAME, key)
    } catch (error) {
      console.error(`IndexedDB delete failed: ${key}`, error)
    }
  }

  /**
   * 清空所有数据
   */
  async clear(): Promise<void> {
    try {
      const db = await this.getDB()
      await db.clear(STORE_NAME)
    } catch (error) {
      console.error('IndexedDB clear failed', error)
    }
  }
}

/**
 * 导出单例实例
 */
export const indexedDBStorage = new IndexedDBStorage()

/**
 * Zustand persist storage 配置
 * 可直接用于 createJSONStorage
 */
export const storage = {
  getItem: async (key: string) => {
    return await indexedDBStorage.getItem(key)
  },
  setItem: async (key: string, value: string) => {
    await indexedDBStorage.setItem(key, value)
  },
  removeItem: async (key: string) => {
    await indexedDBStorage.removeItem(key)
  },
}
